import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import Brand from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import {
  Heart, Users, Armchair, Wallet, Store, Globe, Sparkles, ArrowRight, Check, CalendarClock, ShieldCheck,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1757682065576-f84d3c04d6ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB3ZWRkaW5nJTIwc3dpc3MlMjBhbHBzfGVufDB8fHx8MTc4ODYwMzQxM3ww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Users, t: "Invités & foyers", d: "CRM complet : foyers, régimes, +1, événements et statuts RSVP synchronisés." },
  { icon: MailCheckIcon, t: "RSVP intelligent", d: "Liens personnalisés, QR codes, questions conditionnelles et relances automatiques." },
  { icon: Armchair, t: "Plan de table visuel", d: "Glisser-déposer vos invités confirmés sur des tables, avec alertes de capacité." },
  { icon: Wallet, t: "Budget en CHF", d: "Acomptes, échéances, prévisions et rapports — tout se met à jour automatiquement." },
  { icon: Store, t: "Marketplace romande", d: "Trouvez, comparez et réservez les meilleurs prestataires de Suisse romande." },
  { icon: Sparkles, t: "Assistant IA", d: "Un concierge qui connaît votre mariage : budget, invités, planning du jour J." },
];

function MailCheckIcon(props) { return <Heart {...props} />; }

/* ---- Marketing product mockups (fictional static data only — no real user data) ---- */
function MockInvite() {
  return (
    <div className="rounded-2xl bg-[#fbf8f2] border shadow-xl p-6 w-full">
      <div className="text-center border border-champagne/40 rounded-xl py-6 px-4">
        <div className="eyebrow text-champagne">Save the date</div>
        <div className="font-serif text-3xl text-stone-900 mt-3 leading-tight">Camille<br /><span className="text-xl">&</span><br />Noah</div>
        <div className="text-sm text-stone-500 mt-3 tracking-widest">12.09.2026</div>
        <div className="text-xs text-stone-400 mt-1">Domaine du Léman · Vaud</div>
      </div>
    </div>
  );
}
function MockGuests() {
  const rows = [
    ["Famille Rochat", "4 invités", "Confirmé", "bg-sage/15 text-sage"],
    ["Léa Dubois", "+1", "En attente", "bg-amber-100 text-amber-700"],
    ["Marc & Sofia", "2 invités", "Confirmé", "bg-sage/15 text-sage"],
    ["Oncle Pierre", "1 invité", "Décliné", "bg-destructive/10 text-destructive"],
  ];
  return (
    <div className="rounded-2xl bg-white border shadow-xl p-4 w-full">
      <div className="flex items-center justify-between mb-3"><div className="font-serif text-lg">Invités</div><Users className="w-4 h-4 text-stone-400" /></div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between text-sm border rounded-xl px-3 py-2">
            <div><div className="font-medium text-stone-800">{r[0]}</div><div className="text-xs text-stone-400">{r[1]}</div></div>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${r[3]}`}>{r[2]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
function MockSite() {
  return (
    <div className="rounded-2xl bg-white border shadow-xl overflow-hidden w-full">
      <div className="h-24 bg-gradient-to-br from-accent to-champagne/30 relative">
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-serif text-2xl text-stone-800">Camille & Noah</div>
          <div className="text-[11px] text-stone-500 tracking-widest mt-1">NOUS NOUS MARIONS · 12.09.2026</div>
        </div>
      </div>
      <div className="flex items-center justify-center gap-4 text-[11px] text-stone-500 py-2 border-b">
        <span>Notre histoire</span><span>Programme</span><span>RSVP</span><span>Infos</span>
      </div>
      <div className="p-4 text-xs text-stone-500 text-center">Partagez toutes les informations avec vos invités, en un seul lien.</div>
    </div>
  );
}
function MockSeating() {
  return (
    <div className="rounded-2xl bg-white border shadow-xl p-4 w-full">
      <div className="flex items-center justify-between mb-3"><div className="font-serif text-lg">Plan de table</div><Armchair className="w-4 h-4 text-stone-400" /></div>
      <div className="grid grid-cols-3 gap-3">
        {["Table 1", "Table 2", "Table 3", "Table 4", "Table 5", "Honneur"].map((t, i) => (
          <div key={i} className="aspect-square rounded-full border-2 border-dashed border-stone-200 flex items-center justify-center text-[10px] text-stone-500 relative">
            {t}
            {[0, 1, 2, 3].map((s) => (
              <span key={s} className="absolute w-2.5 h-2.5 rounded-full bg-champagne/60"
                style={{ top: s === 0 ? "-4px" : s === 1 ? "50%" : "auto", bottom: s === 2 ? "-4px" : "auto", left: s === 3 ? "-4px" : s === 0 || s === 2 ? "50%" : "auto", right: s === 1 ? "-4px" : "auto" }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const [hp, setHp] = useState(null);
  useEffect(() => {
    axios.get(`${API}/cms-homepage`).then(({ data }) => setHp(data)).catch(() => setHp({}));
  }, []);
  const cta = user ? (user.wedding_id ? "/app" : "/onboarding") : "/register";
  // Never flash the default image: wait for CMS data, then use the published image (or default only if none).
  const heroImg = hp === null ? null : (hp.hero_image || HERO);
  useEffect(() => {
    if (heroImg) {
      const l = document.createElement("link");
      l.rel = "preload"; l.as = "image"; l.href = heroImg; l.fetchPriority = "high";
      document.head.appendChild(l);
      return () => { try { document.head.removeChild(l); } catch {} };
    }
  }, [heroImg]);
  return (
    <div className="min-h-screen bg-background">
      <header className="h-20 flex items-center justify-between px-6 lg:px-12 max-w-7xl mx-auto">
        <Brand variant="full" />
        <nav className="flex items-center gap-2 lg:gap-4">
          <Link to="/marketplace" className="text-sm text-stone-600 hover:text-stone-900 hidden sm:block">Prestataires</Link>
          <Link to="/pricing" className="text-sm text-stone-600 hover:text-stone-900 hidden sm:block">Tarifs</Link>
          <Link to="/login" className="text-sm text-stone-600 hover:text-stone-900">Connexion</Link>
          <Link to={cta}><Button data-testid="hero-cta-top" className="rounded-full">Créer mon mariage</Button></Link>
        </nav>
      </header>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-10 lg:pt-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <div className="eyebrow mb-4" data-testid="hp-eyebrow">{hp?.hero_eyebrow || "Suisse romande · CHF · fr-CH"}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stone-900" data-testid="hp-title">
            {hp?.hero_title || "Votre mariage. Toute votre organisation. Un seul endroit."}
          </h1>
          <p className="text-lg text-stone-600 mt-6 max-w-xl" data-testid="hp-subtitle">
            {hp?.hero_subtitle || "Invités, budget, prestataires, plan de table, RSVP, planning et jour J — organisés ensemble, avec votre partenaire, dans un espace élégant et vivant."}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link to={cta}><Button size="lg" data-testid="hero-cta" className="rounded-full h-12 px-7">
              Créer mon mariage <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
            <Link to="/marketplace"><Button size="lg" variant="outline" className="rounded-full h-12 px-7">
              Découvrir les prestataires</Button></Link>
          </div>
          <div className="flex items-center gap-5 mt-8 text-sm text-stone-500">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-sage" /> Données privées</span>
            <span className="flex items-center gap-1.5"><CalendarClock className="w-4 h-4 text-sage" /> Compte partenaire séparé</span>
          </div>
        </div>
        <div className="relative animate-fade-up">
          {heroImg ? (
            <img src={heroImg} alt="Table de mariage élégante face aux Alpes suisses" loading="eager"
              fetchPriority="high" data-testid="hero-image"
              className="rounded-3xl w-full h-[420px] lg:h-[540px] object-cover shadow-2xl" />
          ) : (
            <div data-testid="hero-skeleton"
              className="rounded-3xl w-full h-[420px] lg:h-[540px] bg-accent animate-pulse shadow-2xl" />
          )}
          <div className="absolute -bottom-5 -left-5 card-lux p-4 hidden sm:block">
            <div className="eyebrow">Compte à rebours</div>
            <div className="font-mono text-2xl text-stone-900">J-186</div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24" data-testid="product-showcase">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="eyebrow mb-3">Aperçu du produit</div>
          <h2 className="font-serif text-3xl lg:text-4xl text-stone-900">Tout votre mariage, au même endroit.</h2>
          <p className="text-stone-600 mt-4">Invités, faire-part, site de mariage, budget, plan de table et bien plus encore.</p>
          <Link to={cta}><Button size="lg" data-testid="product-cta" className="rounded-full mt-6 h-12 px-7">
            Créer notre mariage gratuitement <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
        </div>
        {/* Mobile: clean vertical stack. Desktop: slightly overlapped product mockups. */}
        <div className="lg:hidden grid sm:grid-cols-2 gap-4 max-w-xl mx-auto">
          <MockInvite /><MockGuests /><MockSite /><MockSeating />
        </div>
        <div className="hidden lg:block relative h-[440px] max-w-5xl mx-auto">
          <div className="absolute left-0 top-6 w-64 -rotate-3 z-20"><MockInvite /></div>
          <div className="absolute left-1/2 -translate-x-1/2 top-0 w-80 z-30"><MockSite /></div>
          <div className="absolute right-0 top-8 w-72 rotate-3 z-20"><MockGuests /></div>
          <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-80 rotate-1 z-40"><MockSeating /></div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="eyebrow mb-3">Tout est connecté</div>
          <h2 className="font-serif text-3xl lg:text-4xl text-stone-900">Un système où chaque module se parle</h2>
          <p className="text-stone-600 mt-4">Une réponse RSVP alimente le plan de table. Un devis accepté crée une
            ligne de budget. Un paiement met à jour votre trésorerie. Plus de tableurs éparpillés.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <div key={i} className="card-lux p-7 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="w-12 h-12 rounded-2xl bg-accent flex items-center justify-center mb-5">
                <f.icon className="w-5 h-5 text-stone-800" /></div>
              <h3 className="font-serif text-xl text-stone-900">{f.t}</h3>
              <p className="text-sm text-stone-600 mt-2 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="eyebrow text-champagne mb-3">Vous êtes prestataire ?</div>
            <h2 className="font-serif text-3xl lg:text-4xl">Rejoignez la marketplace des mariages romands</h2>
            <p className="text-stone-300 mt-4 max-w-lg">Recevez des demandes de devis qualifiées, échangez avec les
              couples et gérez vos réservations depuis un espace dédié.</p>
            <Link to="/register"><Button size="lg" className="rounded-full mt-6 bg-champagne text-stone-900 hover:bg-champagne/90">
              Créer mon profil prestataire</Button></Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {["Devis structurés", "Messagerie intégrée", "Tableau de bord", "Commissions transparentes"].map((x) => (
              <div key={x} className="border border-white/10 rounded-2xl p-5 flex items-center gap-3">
                <Check className="w-4 h-4 text-champagne" /> <span className="text-sm">{x}</span></div>
            ))}
          </div>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-6 lg:px-12 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-stone-500">
        <div>© {new Date().getFullYear()} Notre Oui · notreoui.ch · Suisse romande</div>
        <div className="flex flex-wrap gap-5 gap-y-2">
          <Link to="/pricing" className="hover:text-stone-900">Tarifs</Link>
          <Link to="/marketplace" className="hover:text-stone-900">Prestataires</Link>
          <Link to="/login" className="hover:text-stone-900">Connexion</Link>
          <Link to="/mentions-legales" className="hover:text-stone-900" data-testid="footer-mentions">Mentions légales</Link>
          <Link to="/cgv" className="hover:text-stone-900" data-testid="footer-cgv">CGV</Link>
          <Link to="/confidentialite" className="hover:text-stone-900" data-testid="footer-confidentialite">Confidentialité</Link>
        </div>
      </footer>
    </div>
  );
}
